import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { RefreshToken } from './interfaces/authentication.interface';
import { DebugService } from './debug.service';
import { PermissionsService } from './permissions.service';

export interface LoginChallengeResponse {
  step: 'email_otp' | 'authenticator';
  challengeId: string;
  message?: string;
  alternateMethods?: Array<'email_otp' | 'authenticator'>;
  otpauthUrl?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  /** Evita refrescar en cada navegación si la sesión ya se validó hace poco. */
  private static readonly SESSION_TTL_MS = 45_000;
  private sessionCheckedAt = 0;
  private ensuring$: Observable<boolean> | null = null;

  constructor(
    private http: HttpClient,
    private debug: DebugService,
    private permissionsService: PermissionsService,
    private router: Router,
  ) {}

  public auth(email: string, password: string) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/json',
    });
    return this.http.post<any>(
      `${environment.url_api}/auth/login`,
      { email, password },
      {
        headers,
        observe: 'response',
        withCredentials: true,
      },
    );
  }

  public verifyLogin(challengeId: string, code: string) {
    return this.http.post<any>(
      `${environment.url_api}/auth/login/verify`,
      { challengeId, code },
      { observe: 'response', withCredentials: true },
    );
  }

  public resendLoginOtp(challengeId: string) {
    return this.http.post<LoginChallengeResponse>(
      `${environment.url_api}/auth/login/resend`,
      { challengeId },
      { withCredentials: true },
    );
  }

  public switchLoginFactor(challengeId: string) {
    return this.http.post<LoginChallengeResponse>(
      `${environment.url_api}/auth/login/switch-factor`,
      { challengeId, method: 'email_otp' },
      { withCredentials: true },
    );
  }

  public totpStatus() {
    return this.http.get<{
      enabled: boolean;
      totpEnabled: boolean;
      emailOtpEnabled: boolean;
    }>(`${environment.url_api}/auth/totp/status`, { withCredentials: true });
  }

  public enableEmailOtp() {
    return this.http.post<{ totpEnabled: boolean; emailOtpEnabled: boolean }>(
      `${environment.url_api}/auth/email-otp/enable`,
      {},
      { withCredentials: true },
    );
  }

  public disableEmailOtp() {
    return this.http.post<{ totpEnabled: boolean; emailOtpEnabled: boolean }>(
      `${environment.url_api}/auth/email-otp/disable`,
      {},
      { withCredentials: true },
    );
  }

  public totpEnrollStart() {
    return this.http.post<{ otpauthUrl: string; qrDataUrl: string }>(
      `${environment.url_api}/auth/totp/enroll/start`,
      {},
      { withCredentials: true },
    );
  }

  public totpEnrollConfirm(code: string) {
    return this.http.post<{ enabled: boolean }>(
      `${environment.url_api}/auth/totp/enroll/confirm`,
      { code },
      { withCredentials: true },
    );
  }

  /** Refresh via httpOnly cookie; body is empty. */
  public refresh() {
    return this.http.post<RefreshToken>(
      `${environment.url_api}/auth/refresh`,
      {},
      { observe: 'response', withCredentials: true },
    );
  }

  /**
   * Valida/renueva la sesión (cookie refresh) antes de mostrar la app.
   * Deduplica llamadas concurrentes y cachea éxito breve.
   */
  public ensureSession(): Observable<boolean> {
    if (!localStorage.getItem('user_id')) {
      return throwError(() => new Error('Sin sesión local'));
    }

    const now = Date.now();
    if (now - this.sessionCheckedAt < AuthenticationService.SESSION_TTL_MS) {
      return of(true);
    }

    if (this.ensuring$) {
      return this.ensuring$;
    }

    this.ensuring$ = this.refresh().pipe(
      tap(() => {
        this.sessionCheckedAt = Date.now();
      }),
      map(() => true),
      catchError((err) => {
        this.sessionCheckedAt = 0;
        return throwError(() => err);
      }),
      finalize(() => {
        this.ensuring$ = null;
      }),
      shareReplay(1),
    );

    return this.ensuring$;
  }

  /** Marca la sesión como válida tras login exitoso (evita refresh inmediato). */
  public markSessionValid(): void {
    this.sessionCheckedAt = Date.now();
  }

  public changePassword(email: string, pass: string) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/json',
    });
    return this.http.post<any>(
      `${environment.url_api}/auth/change-password`,
      { email, password: pass },
      { headers, withCredentials: true },
    );
  }

  public sendMailVerification(code: number, email: string) {
    const formData = new FormData();
    formData.append('code', code.toString());
    formData.append('email', email);
    return this.http.post(`${environment.url_api}`, formData, {
      responseType: 'text',
    });
  }

  /** Tokens live in httpOnly cookies — not readable from JS. */
  public getAccessToken(): string | null {
    return null;
  }

  public getRefreshToken(): string | null {
    return null;
  }

  /** No-op: tokens are set by the server via Set-Cookie. */
  public storeTokens(_accessToken: string, _refreshToken: string): void {}

  /** Expiry is signaled by the server with 401. */
  public isAccessTokenExpired(): boolean {
    return false;
  }

  hasLocalSessionHint(): boolean {
    return !!localStorage.getItem('user_id');
  }

  /** Limpia indicador local sin llamar al API (p. ej. refresh fallido). */
  clearLocalSession(): void {
    this.sessionCheckedAt = 0;
    this.ensuring$ = null;
    sessionStorage.clear();
    localStorage.clear();
    this.permissionsService.clearCache();
  }

  /**
   * Cierra sesión en servidor + local y navega a login.
   * @param navigate Si false, solo limpia (útil si ya estás yendo a login).
   */
  public logout(navigate = true): void {
    this.http
      .post(
        `${environment.url_api}/auth/logout`,
        {},
        { withCredentials: true },
      )
      .subscribe({
        error: (e) => this.debug.log('Logout backend error (ignored):', e),
      });

    this.clearLocalSession();

    if (navigate && !this.router.url.startsWith('/login')) {
      this.router.navigate(['/login'], { replaceUrl: true });
    }
  }
}
